import type { JSX } from 'react';
import type { useContractorState } from '@/hooks/useContractorState';
import { Input, Checkbox } from '../ui';

type ContractorState = ReturnType<typeof useContractorState>;

interface Props {
  state: Pick<
    ContractorState,
    | 'annualRevenue'
    | 'setAnnualRevenue'
    | 'businessExpenses'
    | 'setBusinessExpenses'
    | 'retainedProfits'
    | 'setRetainedProfits'
    | 'totalPensionContribution'
    | 'setTotalPensionContribution'
    | 'eligibleForEmploymentAllowance'
    | 'setEligibleForEmploymentAllowance'
    | 'associatedCompanies'
    | 'setAssociatedCompanies'
  >;
}

export function CompanyFormSection({ state }: Props): JSX.Element {
  return (
    <div className="space-y-4">
      <Input
        label="Annual Revenue"
        helperText="Company turnover before VAT"
        type="number"
        prefix="£"
        value={state.annualRevenue}
        onChange={(e) => state.setAnnualRevenue(Number(e.target.value))}
        min={0}
        step={1000}
        fullWidth
        selectAllOnFocus
      />

      <Input
        label="Business Expenses"
        helperText="Deductible expenses (e.g., software, equipment)"
        type="number"
        prefix="£"
        value={state.businessExpenses}
        onChange={(e) => state.setBusinessExpenses(Number(e.target.value))}
        min={0}
        step={100}
        fullWidth
        selectAllOnFocus
      />

      <Input
        label="Retained Profits"
        helperText="Profits retained in company from prior years"
        type="number"
        prefix="£"
        value={state.retainedProfits}
        onChange={(e) => state.setRetainedProfits(Number(e.target.value))}
        min={0}
        step={1000}
        fullWidth
        selectAllOnFocus
      />

      <Input
        label="Company Pension Contribution"
        helperText="Employer pension contribution (reduces corporation tax)"
        type="number"
        prefix="£"
        value={state.totalPensionContribution}
        onChange={(e) => state.setTotalPensionContribution(Number(e.target.value))}
        min={0}
        step={100}
        fullWidth
        selectAllOnFocus
      />

      <Checkbox
        checked={state.eligibleForEmploymentAllowance}
        onChange={(e) => state.setEligibleForEmploymentAllowance(e.target.checked)}
        label="Eligible for Employment Allowance"
        helperText="Requires 2+ employees with at least one earning above £5,000"
      />

      <Input
        label="Associated Companies"
        helperText="Number of associated companies (affects corporation tax thresholds)"
        type="number"
        value={state.associatedCompanies}
        onChange={(e) => state.setAssociatedCompanies(Number(e.target.value))}
        min={0}
        max={10}
        fullWidth
      />
    </div>
  );
}
